import { forwardRef, useImperativeHandle, useState, useRef, useEffect } from 'react';
import CustomCheckbox from '@components/ui/CustomCheckbox';
import CustomSelect from '@components/ui/CustomSelect';
import InputBoxWithUnit from '@components/ui/InputBoxWithUnit';
import Spinner from '@components/ui/Spinner';
import type { FormProps } from '../../types/form.types';
import { useTheme } from '@theme/useTheme';
import { useAuth } from '@contexts/AuthContext';

export interface Page3Handle {
  validate: () => boolean;
}

const Page3 = forwardRef<Page3Handle, FormProps>(({
  formData,
  updateFormData,
  onPrevious,
  onNext,
  currentPage = 3,
  canGoPrevious = true,
  isLoading = false,
  error,
}, ref) => {
  const { colors } = useTheme();
  const { isAuthenticated } = useAuth();
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Refs for each field
  const hypertensionRef = useRef<HTMLDivElement>(null);
  const diabetesRef = useRef<HTMLDivElement>(null);
  const familyHistoryRef = useRef<HTMLDivElement>(null);
  const thyroidFunctionRef = useRef<HTMLDivElement>(null);
  const smokingStatusRef = useRef<HTMLDivElement>(null);
  const alcoholConsumptionRef = useRef<HTMLDivElement>(null);
  const physicalActivityRef = useRef<HTMLDivElement>(null);
  const sleepDurationRef = useRef<HTMLDivElement>(null);

  // Scroll to first error when errors change
  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      
      const fieldRefs: Record<string, React.RefObject<HTMLDivElement | null>> = {
        hypertension: hypertensionRef,
        diabetes: diabetesRef,
        familyHistory: familyHistoryRef,
        thyroidFunction: thyroidFunctionRef,
        smokingStatus: smokingStatusRef,
        alcoholConsumption: alcoholConsumptionRef,
        physicalActivity: physicalActivityRef,
        sleepDuration: sleepDurationRef,
      };

      const refToScroll = fieldRefs[firstErrorField];
      if (refToScroll?.current) {
        refToScroll.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [errors]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Required Yes/No fields
    if (formData.hypertension === null || formData.hypertension === undefined) {
      newErrors.hypertension = 'Please select an option';
    }
    
    if (formData.diabetes === null || formData.diabetes === undefined) {
      newErrors.diabetes = 'Please select an option';
    }
    
    if (formData.familyHistory === null || formData.familyHistory === undefined) {
      newErrors.familyHistory = 'Please select an option';
    }

    // Required select fields
    if (formData.smokingStatus === null || formData.smokingStatus === undefined || formData.smokingStatus === 0) {
      newErrors.smokingStatus = 'Smoking status is required';
    }
    
    if (formData.alcoholConsumption === null || formData.alcoholConsumption === undefined || formData.alcoholConsumption === 0) {
      newErrors.alcoholConsumption = 'Alcohol consumption level is required';
    }
    
    if (formData.physicalActivity === null || formData.physicalActivity === undefined || formData.physicalActivity === 0) {
      newErrors.physicalActivity = 'Physical activity level is required';
    }

    // Sleep Duration validation
    if (formData.sleepDuration === null || formData.sleepDuration === undefined) {
      newErrors.sleepDuration = 'Sleep duration is required';
    } else {
      const sleepValue = formData.sleepDuration;
      if (sleepValue < 0 || sleepValue > 12) {
        newErrors.sleepDuration = 'Sleep duration must be between 0 and 12 hours';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  useImperativeHandle(ref, () => ({
    validate,
  }));

  const clearError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleYesNoChange = (field: keyof typeof formData, value: string) => {
    updateFormData({ [field]: value });
    clearError(field as string);
  };

  const handleNumberChange = (field: keyof typeof formData, value: number) => {
    updateFormData({ [field]: value });
    clearError(field as string);
  };

  const handleReceiveInfoChange = (checked: boolean) => {
    updateFormData({ receiveInfo: checked });
  };

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <div
        className="border p-6 sm:p-8 lg:p-10 overflow-hidden"
        style={{
          backgroundColor: colors.background,
          borderColor: colors.border,
          borderTopRightRadius: '2rem',
          borderBottomLeftRadius: '2rem'
        }}
      >
        <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-4 sm:mb-6" style={{ color: colors.heading }}>
          Medical History & Lifestyle
        </h2>
        <p className="mb-6 sm:mb-8 text-base sm:text-lg" style={{ color: colors.text }}>
          Fields marked with <span className="font-bold" style={{ color: colors.error }}>*</span> are required
        </p>

        <div className="space-y-4 sm:space-y-6">
          {/* First Row - Hypertension & Diabetes */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div ref={hypertensionRef}>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                Hypertension <span style={{ color: colors.error }}>*</span>
              </label>
              <p className="text-sm mb-3" style={{ color: colors.text }}>Do you have high blood pressure?</p>
              <div className="space-y-2">
                {['Yes', 'No'].map((option) => (
                  <CustomCheckbox
                    key={option}
                    description={option}
                    checked={formData.hypertension === option}
                    onChange={() => handleYesNoChange('hypertension', option)}
                  />
                ))}
              </div>
              {errors.hypertension && (
                <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.hypertension}</p>
              )}
            </div>

            <div ref={diabetesRef}>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                Diabetes <span style={{ color: colors.error }}>*</span>
              </label>
              <p className="text-sm mb-3" style={{ color: colors.text }}>Do you have diabetes?</p>
              <div className="space-y-2">
                {['Yes', 'No'].map((option) => (
                  <CustomCheckbox
                    key={option}
                    description={option}
                    checked={formData.diabetes === option}
                    onChange={() => handleYesNoChange('diabetes', option)}
                  />
                ))}
              </div>
              {errors.diabetes && (
                <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.diabetes}</p>
              )}
            </div>
          </div>

          {/* Second Row - Family History & Thyroid Function */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <div ref={familyHistoryRef}>
              <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
                Family History <span style={{ color: colors.error }}>*</span>
              </label>
              <p className="text-sm mb-3" style={{ color: colors.text }}>Do you have a family history of sleep apnea?</p>
              <div className="space-y-2">
                {['Yes', 'No'].map((option) => (
                  <CustomCheckbox
                    key={option}
                    description={option}
                    checked={formData.familyHistory === option}
                    onChange={() => handleYesNoChange('familyHistory', option)}
                  />
                ))}
              </div>
              {errors.familyHistory && (
                <p className="mt-1 text-xs" style={{ color: colors.error }}>{errors.familyHistory}</p>
              )}
            </div>

            <div ref={thyroidFunctionRef}>
              <CustomSelect
                value={formData.thyroidFunction}
                onChange={(value) => {
                  updateFormData({ thyroidFunction: value === 0 ? null : value });
                }}
                options={[
                  { value: 1, label: 'Normal' },
                  { value: 2, label: 'Hypothyroidism' },
                  { value: 3, label: 'Hyperthyroidism' }
                ]}
                placeholder="Select Status"
                label="Thyroid Function (Optional)"
                description="Select your thyroid function status based on medical tests"
              />
            </div>
          </div>

          {/* Smoking Status */}
          <div ref={smokingStatusRef}>
            <CustomSelect
              value={formData.smokingStatus}
              onChange={(value) => {
                updateFormData({ smokingStatus: value === 0 ? null : value });
                clearError('smokingStatus');
              }}
              options={[
                { value: 1, label: 'Never Smoked' },
                { value: 2, label: 'Former Smoker' },
                { value: 3, label: 'Current Smoker' }
              ]}
              placeholder="Select Smoking Status"
              hasError={!!errors.smokingStatus}
              errorMessage={errors.smokingStatus}
              required
              label="Smoking Status"
              description="Select your current smoking status"
            />
          </div>

          {/* Alcohol Consumption */}
          <div ref={alcoholConsumptionRef}>
            <CustomSelect
              value={formData.alcoholConsumption}
              onChange={(value) => {
                updateFormData({ alcoholConsumption: value === 0 ? null : value });
                clearError('alcoholConsumption');
              }}
              options={[
                { value: 1, label: 'None' },
                { value: 2, label: 'Light (1-2 drinks/week)' },
                { value: 3, label: 'Moderate (3-7 drinks/week)' },
                { value: 4, label: 'Heavy (8+ drinks/week)' }
              ]}
              placeholder="Select Alcohol Consumption Level"
              hasError={!!errors.alcoholConsumption}
              errorMessage={errors.alcoholConsumption}
              required
              label="Alcohol Consumption"
              description="Select your typical alcohol consumption level"
            />
          </div>

          {/* Physical Activity */}
          <div ref={physicalActivityRef}>
            <CustomSelect
              value={formData.physicalActivity}
              onChange={(value) => {
                updateFormData({ physicalActivity: value === 0 ? null : value });
                clearError('physicalActivity');
              }}
              options={[
                { value: 1, label: 'Sedentary (Little to no exercise)' },
                { value: 2, label: 'Light (1-3 days/week)' },
                { value: 3, label: 'Moderate (4-6 days/week)' },
                { value: 4, label: 'Active (daily exercise)' }
              ]}
              placeholder="Select Physical Activity Level"
              hasError={!!errors.physicalActivity}
              errorMessage={errors.physicalActivity}
              required
              label="Physical Activity Level"
              description="Select your typical physical activity level"
            />
          </div>

          {/* Sleep Duration */}
          <div ref={sleepDurationRef}>
            <label className="block text-sm font-medium mb-2" style={{ color: colors.text }}>
              Sleep Duration <span style={{ color: colors.error }}>*</span>
            </label>
            <InputBoxWithUnit
              beforeText="Average Sleep Duration"
              isSecureText={false}
              value={formData.sleepDuration?.toString() || ''}
              unitValue="hours/night"
              onChange={(text) => {
                const value = parseInt(text, 10);
                handleNumberChange('sleepDuration', isNaN(value) ? 0 : value);
              }}
              keyBoardType="numeric"
              hasError={!!errors.sleepDuration}
              errorMessage={errors.sleepDuration}
              required
              placeholder="Enter hours (e.g., 7)"
              min={0}
              max={12}
              description="Average hours you sleep per night (Recommended: 7-9 hours for adults)"
            />
          </div>

          {/* Receive Info Checkbox - Only show for logged-in users */}
          {isAuthenticated && (
            <div>
              <CustomCheckbox
                description="I would like to receive information about sleep health and related products"
                checked={formData.receiveInfo}
                onChange={handleReceiveInfoChange}
              />
            </div>
          )}
        </div>

        {/* Info Box */}
        <div
          className="mt-8 p-4 sm:p-6 rounded-lg"
          style={{
            backgroundColor: colors.secondaryButton,
            borderTopRightRadius: '1.5rem',
            borderBottomLeftRadius: '1.5rem'
          }}
        >
          <h3 className="font-semibold mb-3 text-base sm:text-lg" style={{ color: colors.heading }}>
            Understanding Risk Factors:
          </h3>
          <ul className="text-sm sm:text-base space-y-2" style={{ color: colors.text }}>
            <li>• <strong>Hypertension & Diabetes:</strong> Common comorbidities with sleep apnea</li>
            <li>• <strong>Family History:</strong> Genetic predisposition increases risk</li>
            <li>• <strong>Smoking & Alcohol:</strong> Can worsen airway obstruction and sleep quality</li>
            <li>• <strong>Physical Activity:</strong> Regular exercise may reduce sleep apnea severity</li>
            <li>• <strong>Sleep Duration:</strong> Both too little and too much sleep can indicate issues</li>
          </ul>
        </div>

        {/* Navigation Buttons */}
        {onPrevious && onNext && (
          <div
            className="mt-6 sm:mt-8 pt-6 border-t"
            style={{ borderColor: colors.border }}
          >
            {/* Error Display */}
            {error && (
              <div
                className="mb-4 p-3 rounded-lg border"
                style={{
                  backgroundColor: colors.error + '20',
                  borderColor: colors.error
                }}
              >
                <p className="text-sm" style={{ color: colors.error }}>{error}</p>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={onPrevious}
                disabled={!canGoPrevious || isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium transition-all duration-200 ${canGoPrevious && !isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: canGoPrevious && !isLoading ? colors.secondaryButton : colors.input,
                  color: canGoPrevious && !isLoading ? colors.text : colors.text + '80',
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                Previous
              </button>

              {/* Page Indicators - Centered */}
              <div className="flex items-center justify-center space-x-2">
                {[0, 1, 2, 3].map((page) => (
                  <div
                    key={page}
                    className={`w-2 h-2 rounded-full transition-all duration-200 ${page === currentPage
                      ? 'scale-110'
                      : page < currentPage
                        ? 'opacity-60'
                        : 'opacity-30'
                      }`}
                    style={{
                      backgroundColor:
                        page === currentPage
                          ? colors.primary
                          : page < currentPage
                            ? colors.primary
                            : colors.border,
                    }}
                  />
                ))}
              </div>

              <button
                onClick={onNext}
                disabled={isLoading}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 font-medium text-white transition-all duration-200 ${!isLoading
                  ? 'hover:shadow-lg transform hover:-translate-y-0.5'
                  : 'cursor-not-allowed'
                  }`}
                style={{
                  backgroundColor: !isLoading ? colors.primaryButton : colors.border,
                  borderTopRightRadius: '1rem',
                  borderBottomLeftRadius: '1rem'
                }}
              >
                {isLoading ? (
                  <Spinner size="sm" color="white" text="Processing..." showText={true} />
                ) : currentPage === 3 ? (
                  'Complete Assessment'
                ) : (
                  'Next'
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

Page3.displayName = 'Page3';

export default Page3;
